'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

const HERO_CATEGORIES = [
  {
    tag: '[BLOCKCHAIN FORENSICS]',
    title: 'Trace Every\nSatoshi',
    sub: 'On-chain transaction mapping across BTC, ETH, USDT, and 40+ networks.',
    icon: 'MagnifyingGlassIcon',
  },
  {
    tag: '[SYNDICATE RECON]',
    title: 'Map the\nInfrastructure',
    sub: 'Phishing kit attribution, domain clustering, and operator identification.',
    icon: 'GlobeAltIcon',
  },
  {
    tag: '[ASSET TRIAGE]',
    title: 'Build the\nEvidentiary File',
    sub: 'Court-ready forensic packages for law enforcement and civil litigation.',
    icon: 'ShieldCheckIcon',
  },
];

export default function HeroSection() {
  const [searchValue, setSearchValue] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [cardVisible, setCardVisible] = useState([false, false, false]);
  const router = useRouter();

  useEffect(() => {
    const timers = HERO_CATEGORIES.map((_, i) =>
      setTimeout(() => {
        setCardVisible((prev) => {
          const next = [...prev];
          next[i] = true;
          return next;
        });
      }, 600 + i * 200)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      setIsLoading(true);
      router.push(`/verify?q=${encodeURIComponent(searchValue.trim())}`);
    }
  };

  return (
    <section
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-20"
      aria-label="ForensicDesk hero — intelligence operations"
    >
      {/* Noise overlay */}
      <div className="fixed inset-0 noise-overlay pointer-events-none z-0 opacity-60" aria-hidden="true" />

      {/* Ambient glow blobs */}
      <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden" aria-hidden="true">
        <div
          className="absolute top-[-8%] right-[-4%] w-72 md:w-96 h-72 md:h-96 rounded-full ambient-drift"
          style={{ background: 'rgba(220, 38, 38, 0.07)', filter: 'blur(80px)' }}
        />
        <div
          className="absolute bottom-[-10%] left-[-6%] w-80 md:w-[500px] h-80 md:h-[500px] rounded-full ambient-drift-slow"
          style={{ background: 'rgba(39, 39, 42, 0.6)', filter: 'blur(100px)' }}
        />
        <div
          className="absolute top-[40%] left-[30%] w-64 h-64 rounded-full"
          style={{ background: 'rgba(22, 163, 74, 0.04)', filter: 'blur(60px)', animation: 'ambient-drift 20s ease-in-out infinite' }}
        />
      </div>

      {/* Grid line background */}
      <div className="absolute inset-0 grid-line-bg opacity-30 z-0" aria-hidden="true" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 w-full py-16 md:py-24">

        {/* Classification badge */}
        <div
          className="glass-card inline-flex items-center gap-3 px-4 py-1.5 mb-8 opacity-0 animate-fade-in"
          style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-accent threat-pulse shrink-0" />
          <span className="bracket-tag text-muted-foreground">
            [GLOBAL THREAT // ACTIVE OPERATIONS // EST. 2021]
          </span>
        </div>

        {/* Main headline */}
        <h1
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter uppercase leading-[0.9] text-foreground mb-4 opacity-0 animate-fade-in"
          style={{ animationDelay: '0.25s', animationFillMode: 'forwards' }}
        >
          <span className="block">Crypto Crime</span>
          <span className="block text-gradient-crimson">Has an Enemy.</span>
        </h1>

        <p
          className="text-base md:text-lg text-muted-foreground max-w-2xl mb-10 font-light leading-relaxed opacity-0 animate-fade-in"
          style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}
        >
          ForensicDesk deploys military-grade blockchain forensics and syndicate
          reconnaissance against industrialized crypto fraud operations.
          We trace. We identify. We build the case.
        </p>

        {/* Omni-search bar */}
        <form
          onSubmit={handleSearch}
          className="flex flex-col sm:flex-row gap-3 max-w-2xl mb-6 opacity-0 animate-fade-in"
          style={{ animationDelay: '0.55s', animationFillMode: 'forwards' }}
          aria-label="Threat intelligence search"
        >
          <div
            className={`flex-1 flex items-center gap-3 border px-4 py-3 transition-all duration-300 ${
              searchFocused ? 'border-primary bg-card' : 'border-border bg-input'
            }`}
          >
            <Icon name="MagnifyingGlassIcon" size={16} className="text-muted-foreground shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              placeholder="Enter wallet address, URL, or email header..."
              className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none terminal-text min-w-0"
              aria-label="Search for suspicious wallet, URL, or email"
            />
            {searchValue && (
              <button
                type="button"
                onClick={() => setSearchValue('')}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Clear search"
              >
                <Icon name="XMarkIcon" size={14} />
              </button>
            )}
          </div>
          <button
            type="submit"
            disabled={!searchValue.trim() || isLoading}
            className="shine-effect flex items-center justify-center gap-2 bg-primary hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-primary-foreground px-6 py-3 bracket-tag transition-colors duration-200 whitespace-nowrap min-h-[44px]"
          >
            {isLoading ? (
              <>
                <span className="w-1.5 h-1.5 rounded-full bg-primary-foreground animate-pulse" />
                ANALYZING...
              </>
            ) : (
              <>
                RUN ANALYSIS
              </>
            )}
          </button>
        </form>

        {/* Ghost CTA */}
        <div
          className="flex items-center gap-4 mb-16 opacity-0 animate-fade-in"
          style={{ animationDelay: '0.65s', animationFillMode: 'forwards' }}
        >
          <Link
            href="/intake"
            className="flex items-center gap-2 border border-border hover:border-primary text-muted-foreground hover:text-foreground px-5 py-2.5 bracket-tag transition-all duration-200 min-h-[44px]"
          >
            <Icon name="DocumentTextIcon" size={14} />
            REPORT INCIDENT
          </Link>
          <span className="bracket-tag text-muted-foreground hidden sm:block">
            // ENCRYPTED INTAKE CHANNEL
          </span>
        </div>

        {/* Staggered capability cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {HERO_CATEGORIES.map((cat, i) => (
            <article
              key={cat.tag}
              className={`group relative glass-card p-5 border border-border capability-card-hover transition-all duration-700 ${
                cardVisible[i] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              } ${i === 1 ? 'md:-mt-6 z-20' : ''}`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              {/* Hover scan line */}
              <div className="absolute top-0 left-0 w-full h-px bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" aria-hidden="true" />

              <div className="flex items-start justify-between mb-4">
                <span className="bracket-tag text-primary">{cat.tag}</span>
                <Icon name={cat.icon as Parameters<typeof Icon>[0]['name']} size={16} className="text-muted-foreground group-hover:text-primary transition-colors duration-300" />
              </div>

              <h2 className="text-xl font-black tracking-tighter uppercase text-foreground leading-tight mb-3 whitespace-pre-line">
                {cat.title}
              </h2>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                {cat.sub}
              </p>

              {/* Bottom crimson line on hover */}
              <div className="absolute bottom-0 left-0 w-full h-px bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-right" aria-hidden="true" />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}